/**
 * User Table Component
 * Main table displaying all users with sortable columns, checkboxes, and badges
 * Following the pattern from LocationTable.tsx
 */

import React, { useState, useRef } from 'react';
import { View, Text, ScrollView, TouchableOpacity, Pressable, StyleSheet, Platform, NativeSyntheticEvent, NativeScrollEvent } from 'react-native';
import Svg, { Path } from 'react-native-svg';
import LHCheckBox from '../../../../components/LHCheckBox';
import AdminStatusBadge from '../../components/AdminStatusBadge';
import NewUserBadge from './NewUserBadge';
import { AdminColors } from '../../constants/adminColors';
import FontFamily from '../../../../constants/fonts';
import { useTheme } from '../../../../theme/useTheme';
import { User, SortConfig } from './userManagementTypes';
import { formatDate } from './userManagementUtils';

interface UserTableProps {
  users: User[];
  selectedUserIds: string[];
  sortConfig: SortConfig;
  onToggleSelection: (userId: string) => void;
  onSelectAll: () => void;
  onSort: (column: keyof User) => void;
  onRowClick: (user: User) => void;
  onClearNewTag: (userId: string) => void;
  onLoadMore?: () => void;
  hasMore?: boolean;
  isLoadingMore?: boolean;
  searchQuery?: string;
  columnFilters?: {
    location: string[];
    accessLevel: string[];
    activeStatus: string[];
  };
  onColumnFilterChange?: (field: 'location' | 'accessLevel' | 'activeStatus', values: string[]) => void;
  locationOptions?: string[];
  accessLevelOptions?: string[];
}

// ─── Column Configuration ─────────────────────────────────────────────────────
// Using flex with minWidth ensures consistent layout without fixed widths
const COLUMN_CONFIG = {
  checkbox: { flex: 0.3, minWidth: 50 },
  id: { flex: 0.5, minWidth: 70 },
  userEmail: { flex: 1.5, minWidth: 220 },
  location: { flex: 1.2, minWidth: 180 },
  accessLevel: { flex: 1, minWidth: 140 },
  gpid: { flex: 0.7, minWidth: 100 },
  locationId: { flex: 0.7, minWidth: 100 },
  lastUpdateTime: { flex: 1, minWidth: 150 },
  naviJoinDate: { flex: 1, minWidth: 140 },
  activeStatus: { flex: 0.7, minWidth: 100 },
  sustainTeam: { flex: 0.8, minWidth: 120 },
  trainingColumn: { flex: 1, minWidth: 116 }, // Equal width within training group
  trainingGroup: { flex: 2.4, minWidth: 350 }, // Container for all 3 training columns
  newStatus: { flex: 0.6, minWidth: 90 },
} as const;

const SortArrows = ({ column, sortConfig }: { column: keyof User; sortConfig: SortConfig }) => {
  const isActive = sortConfig.column === column;
  const direction = sortConfig.direction;
  
  return (
    <View style={styles.sortArrows}>
      <Text style={[
        styles.arrow, 
        isActive && direction === 'asc' && styles.arrowActive
      ]}>▲</Text>
      <Text style={[
        styles.arrow, 
        isActive && direction === 'desc' && styles.arrowActive
      ]}>▼</Text>
    </View>
  );
};

// ─── Filter Icon ─────────────────────────────────────────────────────────────
const FilterIcon = ({ active }: { active: boolean }) => (
  <Svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke={active ? AdminColors.blue700 : AdminColors.gray350} strokeWidth="2.5">
    <Path d="M22 3H2l8 9.46V19l4 2v-8.54z" />
  </Svg>
);

const UserTable: React.FC<UserTableProps> = ({
  users,
  selectedUserIds,
  sortConfig,
  onToggleSelection,
  onSelectAll,
  onSort,
  onRowClick,
  onClearNewTag,
  onLoadMore,
  hasMore = false,
  isLoadingMore = false,
  searchQuery = '',
  columnFilters = { location: [], accessLevel: [], activeStatus: [] },
  onColumnFilterChange,
  locationOptions = [],
  accessLevelOptions = [],
}) => {
  const { colors, resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark' && Platform.OS !== 'web';

  type FilterField = 'location' | 'accessLevel' | 'activeStatus';

  const tableContainerRef = useRef<View>(null);
  const locationBtnRef = useRef<View>(null);
  const accessLevelBtnRef = useRef<View>(null);
  const activeStatusBtnRef = useRef<View>(null);

  const [filterDropdown, setFilterDropdown] = useState<{
    field: FilterField;
    top: number;
    left: number;
  } | null>(null);

  const openDropdown = (field: FilterField, btnRef: React.RefObject<any>) => {
    if (filterDropdown?.field === field) {
      setFilterDropdown(null);
      return;
    }
    btnRef.current?.measureInWindow((bx: number, by: number, _bw: number, bh: number) => {
      tableContainerRef.current?.measureInWindow((tx: number, ty: number) => {
        setFilterDropdown({ field, top: by - ty + bh + 4, left: bx - tx });
      });
    });
  };

  const toggleFilterValue = (field: FilterField, value: string) => {
    const current = columnFilters[field];
    const updated = current.includes(value)
      ? current.filter(v => v !== value)
      : [...current, value];
    onColumnFilterChange?.(field, updated);
    setFilterDropdown(null);
  };

  const allSelected = users.length > 0 && selectedUserIds.length === users.length;
  const someSelected = selectedUserIds.length > 0 && selectedUserIds.length < users.length;

  // ── Infinite scroll: fire onLoadMore when user scrolls near the bottom ────
  const handleScroll = (e: NativeSyntheticEvent<NativeScrollEvent>) => {
    if (!onLoadMore || !hasMore || isLoadingMore) return;
    const { layoutMeasurement, contentOffset, contentSize } = e.nativeEvent;
    const distanceFromBottom = contentSize.height - (contentOffset.y + layoutMeasurement.height);
    if (distanceFromBottom < 100) {
      onLoadMore();
    }
  };

  const renderHeaderCell = (
    label: string, 
    column: keyof User, 
    config: { flex: number; minWidth: number }
  ) => (
    <View style={[styles.headerCell, { flex: config.flex, minWidth: config.minWidth }]}>
      <TouchableOpacity 
        style={styles.headerContent}
        onPress={() => onSort(column)}
        activeOpacity={0.7}
      >
        <Text style={[
          styles.headerText,
          sortConfig.column === column && styles.headerTextActive
        ]}>
          {label}
        </Text>
        <SortArrows column={column} sortConfig={sortConfig} />
      </TouchableOpacity>
    </View>
  );

  return (
    <View ref={tableContainerRef} style={[styles.tableContainer, isDark && { backgroundColor: colors.background.surface }]}>
      {/* Header */}
      <ScrollView horizontal showsHorizontalScrollIndicator={true}>
        <View>
          {/* Column Headers */}
          <View style={[styles.headerRow, isDark && { backgroundColor: colors.background.surface, borderBottomColor: colors.border.divider }]}>
            {/* Checkbox */}
            <View style={[styles.headerCell, { flex: COLUMN_CONFIG.checkbox.flex, minWidth: COLUMN_CONFIG.checkbox.minWidth }]}>
              <LHCheckBox
                value={allSelected}
                onValueChange={onSelectAll}
              />
            </View>
            
            {renderHeaderCell('User Email', 'userEmail', COLUMN_CONFIG.userEmail)}

            {/* Location Header – with column filter */}
            <View style={[styles.headerCell, { flex: COLUMN_CONFIG.location.flex, minWidth: COLUMN_CONFIG.location.minWidth }]}>
              <View style={styles.filterHeaderTop}>
                <TouchableOpacity style={[styles.headerContent, { flex: 1 }]} onPress={() => onSort('location')} activeOpacity={0.7}>
                  <Text style={[styles.headerText, sortConfig.column === 'location' && styles.headerTextActive]}>Location</Text>
                  <SortArrows column="location" sortConfig={sortConfig} />
                </TouchableOpacity>
                <View ref={locationBtnRef}>
                  <TouchableOpacity
                    style={[styles.filterTrigger, columnFilters.location.length > 0 && styles.filterTriggerActive]}
                    onPress={() => openDropdown('location', locationBtnRef)}
                    activeOpacity={0.7}
                  >
                    <FilterIcon active={columnFilters.location.length > 0} />
                    {columnFilters.location.length > 0 && (
                      <View style={styles.filterCountBadge}>
                        <Text style={styles.filterCountText}>{columnFilters.location.length}</Text>
                      </View>
                    )}
                  </TouchableOpacity>
                </View>
              </View>
            </View>

            {/* Access Level Header – with column filter */}
            <View style={[styles.headerCell, { flex: COLUMN_CONFIG.accessLevel.flex, minWidth: COLUMN_CONFIG.accessLevel.minWidth }]}>
              <View style={styles.filterHeaderTop}>
                <TouchableOpacity style={[styles.headerContent, { flex: 1 }]} onPress={() => onSort('accessLevel')} activeOpacity={0.7}>
                  <Text style={[styles.headerText, sortConfig.column === 'accessLevel' && styles.headerTextActive]}>Access Level</Text>
                  <SortArrows column="accessLevel" sortConfig={sortConfig} />
                </TouchableOpacity>
                <View ref={accessLevelBtnRef}>
                  <TouchableOpacity
                    style={[styles.filterTrigger, columnFilters.accessLevel.length > 0 && styles.filterTriggerActive]}
                    onPress={() => openDropdown('accessLevel', accessLevelBtnRef)}
                    activeOpacity={0.7}
                  >
                    <FilterIcon active={columnFilters.accessLevel.length > 0} />
                    {columnFilters.accessLevel.length > 0 && (
                      <View style={styles.filterCountBadge}>
                        <Text style={styles.filterCountText}>{columnFilters.accessLevel.length}</Text>
                      </View>
                    )}
                  </TouchableOpacity>
                </View>
              </View>
            </View>

            {renderHeaderCell('GPID', 'gpid', COLUMN_CONFIG.gpid)}
            {renderHeaderCell('Location ID', 'locationId', COLUMN_CONFIG.locationId)}
            {renderHeaderCell('Last Update Time', 'lastUpdateTime', COLUMN_CONFIG.lastUpdateTime)}
            {renderHeaderCell('Navi Join Date', 'naviJoinDate', COLUMN_CONFIG.naviJoinDate)}
            {/* Active Status Header – with column filter */}
            <View style={[styles.headerCell, { flex: COLUMN_CONFIG.activeStatus.flex, minWidth: COLUMN_CONFIG.activeStatus.minWidth }]}>
              <View style={styles.filterHeaderTop}>
                <TouchableOpacity style={[styles.headerContent, { flex: 1 }]} onPress={() => onSort('activeStatus')} activeOpacity={0.7}>
                  <Text style={[styles.headerText, sortConfig.column === 'activeStatus' && styles.headerTextActive]}>Active</Text>
                  <SortArrows column="activeStatus" sortConfig={sortConfig} />
                </TouchableOpacity>
                <View ref={activeStatusBtnRef}>
                  <TouchableOpacity
                    style={[styles.filterTrigger, columnFilters.activeStatus.length > 0 && styles.filterTriggerActive]}
                    onPress={() => openDropdown('activeStatus', activeStatusBtnRef)}
                    activeOpacity={0.7}
                  >
                    <FilterIcon active={columnFilters.activeStatus.length > 0} />
                    {columnFilters.activeStatus.length > 0 && (
                      <View style={styles.filterCountBadge}>
                        <Text style={styles.filterCountText}>{columnFilters.activeStatus.length}</Text>
                      </View>
                    )}
                  </TouchableOpacity>
                </View>
              </View>
            </View>

            {renderHeaderCell('Sustain Team', 'sustainTeamMember', COLUMN_CONFIG.sustainTeam)}
            
            {/* Training Header (grouped) */}
            <View style={[styles.headerCell, { flex: COLUMN_CONFIG.trainingGroup.flex, minWidth: COLUMN_CONFIG.trainingGroup.minWidth }]}>
              <View style={[styles.trainingHeader, {width: '100%'}]}>
                <Text style={styles.trainingHeaderText}>Training</Text>
              </View>
              <View style={styles.trainingSubHeaders}>
                <TouchableOpacity 
                  style={[styles.trainingSubCell, { flex: COLUMN_CONFIG.trainingColumn.flex, minWidth: COLUMN_CONFIG.trainingColumn.minWidth }]}
                  onPress={() => onSort('fundamentals')}
                  activeOpacity={0.7}
                >
                  <Text style={[
                    styles.headerText,
                    sortConfig.column === 'fundamentals' && styles.headerTextActive
                  ]}>
                    FUNDAMENTALS
                  </Text>
                  <SortArrows column="fundamentals" sortConfig={sortConfig} />
                </TouchableOpacity>
                
                <TouchableOpacity 
                  style={[styles.trainingSubCell, { flex: COLUMN_CONFIG.trainingColumn.flex, minWidth: COLUMN_CONFIG.trainingColumn.minWidth }]}
                  onPress={() => onSort('lighthouse')}
                  activeOpacity={0.7}
                >
                  <Text style={[
                    styles.headerText,
                    sortConfig.column === 'lighthouse' && styles.headerTextActive
                  ]}>
                    LIGHTHOUSE
                  </Text>
                  <SortArrows column="lighthouse" sortConfig={sortConfig} />
                </TouchableOpacity>
                
                <TouchableOpacity 
                  style={[styles.trainingSubCell, { flex: COLUMN_CONFIG.trainingColumn.flex, minWidth: COLUMN_CONFIG.trainingColumn.minWidth }]}
                  onPress={() => onSort('advFundamentals')}
                  activeOpacity={0.7}
                >
                  <Text style={[
                    styles.headerText,
                    sortConfig.column === 'advFundamentals' && styles.headerTextActive
                  ]}>
                    ADV FUNDAMENTALS
                  </Text>
                  <SortArrows column="advFundamentals" sortConfig={sortConfig} />
                </TouchableOpacity>
              </View>
            </View>
            
            {/* New Status */}
            {renderHeaderCell('New Status', 'newStatus', COLUMN_CONFIG.newStatus)}
          </View>

          {/* Table Body */}
          <ScrollView
            style={styles.tableBody}
            onScroll={handleScroll}
            scrollEventThrottle={200}
          >
            {users.length === 0 && (
              searchQuery.trim().length > 0 ||
              columnFilters.location.length > 0 ||
              columnFilters.accessLevel.length > 0 ||
              columnFilters.activeStatus.length > 0
            ) ? (
              <View style={styles.emptyState}>
                <Text style={styles.emptyStateText}>User not found. Please verify the user ID.</Text>
              </View>
            ) : (
              users.map((user, index) => {
              const isSelected = selectedUserIds.includes(user.id);

              return (
                <TouchableOpacity
                  key={`${user.id}_${index}`}
                  style={[
                    styles.row,
                    isSelected && styles.rowSelected,
                    isDark && { backgroundColor: colors.background.surface, borderBottomColor: colors.border.divider },
                    isDark && isSelected && { backgroundColor: 'rgba(61,139,255,0.12)' },
                  ]}
                  onPress={() => onRowClick(user)}
                  activeOpacity={0.8}
                >
                  {/* Checkbox – single Pressable to avoid nested-touchable double-fire on web */}
                  <Pressable
                    style={[styles.cell, { flex: COLUMN_CONFIG.checkbox.flex, minWidth: COLUMN_CONFIG.checkbox.minWidth }]}
                    onPress={(e) => {
                      e.stopPropagation();
                      onToggleSelection(user.id);
                    }}
                  >
                    <View pointerEvents="none">
                      <LHCheckBox
                        value={isSelected}
                        onValueChange={() => {}}
                      />
                    </View>
                  </Pressable>
                  
                  <View style={[styles.cell, { flex: COLUMN_CONFIG.userEmail.flex, minWidth: COLUMN_CONFIG.userEmail.minWidth }]}>
                    <Text style={[styles.cellText, isDark && { color: colors.text.primary }]} numberOfLines={1}>{user.userEmail}</Text>
                  </View>

                  <View style={[styles.cell, { flex: COLUMN_CONFIG.location.flex, minWidth: COLUMN_CONFIG.location.minWidth }]}>
                    <Text style={[styles.cellText, isDark && { color: colors.text.primary }]} numberOfLines={1}>
                      {user.location.join(', ')}
                    </Text>
                  </View>

                  <View style={[styles.cell, { flex: COLUMN_CONFIG.accessLevel.flex, minWidth: COLUMN_CONFIG.accessLevel.minWidth }]}>
                    <Text style={[styles.cellText, isDark && { color: colors.text.primary }]}>{user.accessLevel}</Text>
                  </View>

                  <View style={[styles.cell, { flex: COLUMN_CONFIG.gpid.flex, minWidth: COLUMN_CONFIG.gpid.minWidth }]}>
                    <Text style={[styles.cellText, isDark && { color: colors.text.primary }]}>{user.gpid}</Text>
                  </View>

                  <View style={[styles.cell, { flex: COLUMN_CONFIG.locationId.flex, minWidth: COLUMN_CONFIG.locationId.minWidth }]}>
                    <Text style={[styles.cellText, isDark && { color: colors.text.primary }]}>{user.locationId}</Text>
                  </View>

                  <View style={[styles.cell, { flex: COLUMN_CONFIG.lastUpdateTime.flex, minWidth: COLUMN_CONFIG.lastUpdateTime.minWidth }]}>
                    <Text style={[styles.cellText, isDark && { color: colors.text.primary }]}>{formatDate(user.lastUpdateTime)}</Text>
                  </View>

                  <View style={[styles.cell, { flex: COLUMN_CONFIG.naviJoinDate.flex, minWidth: COLUMN_CONFIG.naviJoinDate.minWidth }]}>
                    <Text style={[styles.cellText, isDark && { color: colors.text.primary }]}>{formatDate(user.naviJoinDate)}</Text>
                  </View>
                  
                  <View style={[styles.cell, { flex: COLUMN_CONFIG.activeStatus.flex, minWidth: COLUMN_CONFIG.activeStatus.minWidth }]}>
                    <AdminStatusBadge 
                      status={user.activeStatus === 'Active' ? 'active' : 'inactive'}
                    />
                  </View>
                  
                  <View style={[styles.cell, { flex: COLUMN_CONFIG.sustainTeam.flex, minWidth: COLUMN_CONFIG.sustainTeam.minWidth }]}>
                    <View style={[
                      styles.badge,
                      user.sustainTeamMember === true ? styles.badgeYes : styles.badgeNo,
                      isDark && user.sustainTeamMember === true && { backgroundColor: 'rgba(61,139,255,0.2)' },
                      isDark && user.sustainTeamMember !== true && { backgroundColor: colors.border.divider },
                    ]}>
                      <Text style={[
                        styles.badgeText,
                        user.sustainTeamMember === true ? styles.badgeTextYes : styles.badgeTextNo,
                        isDark && user.sustainTeamMember === true && { color: colors.brand.primary },
                        isDark && user.sustainTeamMember !== true && { color: colors.text.secondary },
                      ]}>
                        {user.sustainTeamMember === true ? 'Yes' : 'No'}
                      </Text>
                    </View>
                  </View>
                  
                  {/* Training Dates - Grouped container to match header structure */}
                  <View style={[styles.cell, { flex: COLUMN_CONFIG.trainingGroup.flex, minWidth: COLUMN_CONFIG.trainingGroup.minWidth }]}>
                    <View style={styles.trainingRow}>
                      <View style={[styles.trainingCell, { flex: COLUMN_CONFIG.trainingColumn.flex, minWidth: COLUMN_CONFIG.trainingColumn.minWidth }]}>
                        <Text style={[styles.cellText, isDark && { color: colors.text.primary }]}>{formatDate(user.fundamentals)}</Text>
                      </View>

                      <View style={[styles.trainingCell, { flex: COLUMN_CONFIG.trainingColumn.flex, minWidth: COLUMN_CONFIG.trainingColumn.minWidth }]}>
                        <Text style={[styles.cellText, isDark && { color: colors.text.primary }]}>{formatDate(user.lighthouse)}</Text>
                      </View>

                      <View style={[styles.trainingCell, { flex: COLUMN_CONFIG.trainingColumn.flex, minWidth: COLUMN_CONFIG.trainingColumn.minWidth }]}>
                        <Text style={[styles.cellText, isDark && { color: colors.text.primary }]}>{formatDate(user.advFundamentals)}</Text>
                      </View>
                    </View>
                  </View>
                  
                  {/* New Status */}
                  <View style={[styles.cell, { flex: COLUMN_CONFIG.newStatus.flex, minWidth: COLUMN_CONFIG.newStatus.minWidth }]}>
                    {user.newStatus && (
                      <TouchableOpacity
                        onPress={(e) => {
                          e.stopPropagation();
                        }}
                        activeOpacity={1}
                      >
                        <NewUserBadge onIgnore={() => onClearNewTag(user.id)} />
                      </TouchableOpacity>
                    )}
                  </View>
                </TouchableOpacity>
              );
            })
            )}

          </ScrollView>
        </View>
      </ScrollView>

      {/* Backdrop – closes dropdown on outside click */}
      {filterDropdown && (
        <TouchableOpacity
          style={[StyleSheet.absoluteFillObject, { zIndex: 99 }]}
          onPress={() => setFilterDropdown(null)}
          activeOpacity={1}
        />
      )}

      {/* Floating filter dropdown – renders over the table body as an overlay */}
      {filterDropdown && (
        <View
          style={[styles.floatingDropdown, { top: filterDropdown.top, left: filterDropdown.left }, isDark && { backgroundColor: colors.background.elevated, borderColor: colors.border.default }]}
          onStartShouldSetResponder={() => true}
        >
          {filterDropdown.field === 'location' && (
            <>
              <ScrollView style={styles.filterOptionsList} nestedScrollEnabled showsVerticalScrollIndicator={true} persistentScrollbar={true}>
                {locationOptions.map(opt => {
                  const checked = columnFilters.location.includes(opt);
                  return (
                    <TouchableOpacity key={opt} style={styles.filterOption} onPress={() => toggleFilterValue('location', opt)} activeOpacity={0.7}>
                      <View style={[styles.filterCheckbox, checked && styles.filterCheckboxOn, isDark && !checked && { borderColor: colors.border.default }]}>
                        {checked && <Text style={styles.filterCheckMark}>✓</Text>}
                      </View>
                      <Text style={[styles.filterOptionText, checked && styles.filterOptionTextOn, isDark && { color: colors.text.secondary }, isDark && checked && { color: colors.brand.primary }]} numberOfLines={1}>{opt}</Text>
                    </TouchableOpacity>
                  );
                })}
              </ScrollView>
              {columnFilters.location.length > 0 && (
                <TouchableOpacity style={styles.filterClearRow} onPress={() => { onColumnFilterChange?.('location', []); setFilterDropdown(null); }} activeOpacity={0.7}>
                  <Text style={styles.filterClearText}>Clear filter</Text>
                </TouchableOpacity>
              )}
            </>
          )}
          {filterDropdown.field === 'accessLevel' && (
            <>
              <ScrollView style={styles.filterOptionsList} nestedScrollEnabled showsVerticalScrollIndicator={true} persistentScrollbar={true}>
                {accessLevelOptions.map(opt => {
                  const checked = columnFilters.accessLevel.includes(opt);
                  return (
                    <TouchableOpacity key={opt} style={styles.filterOption} onPress={() => toggleFilterValue('accessLevel', opt)} activeOpacity={0.7}>
                      <View style={[styles.filterCheckbox, checked && styles.filterCheckboxOn, isDark && !checked && { borderColor: colors.border.default }]}>
                        {checked && <Text style={styles.filterCheckMark}>✓</Text>}
                      </View>
                      <Text style={[styles.filterOptionText, checked && styles.filterOptionTextOn, isDark && { color: colors.text.secondary }, isDark && checked && { color: colors.brand.primary }]}>{opt}</Text>
                    </TouchableOpacity>
                  );
                })}
              </ScrollView>
              {columnFilters.accessLevel.length > 0 && (
                <TouchableOpacity style={styles.filterClearRow} onPress={() => { onColumnFilterChange?.('accessLevel', []); setFilterDropdown(null); }} activeOpacity={0.7}>
                  <Text style={styles.filterClearText}>Clear filter</Text>
                </TouchableOpacity>
              )}
            </>
          )}
          {filterDropdown.field === 'activeStatus' && (
            <>
              <ScrollView style={styles.filterOptionsList} nestedScrollEnabled showsVerticalScrollIndicator={true} persistentScrollbar={true}>
                {['Active', 'Inactive'].map(opt => {
                  const checked = columnFilters.activeStatus.includes(opt);
                  return (
                    <TouchableOpacity key={opt} style={styles.filterOption} onPress={() => toggleFilterValue('activeStatus', opt)} activeOpacity={0.7}>
                      <View style={[styles.filterCheckbox, checked && styles.filterCheckboxOn, isDark && !checked && { borderColor: colors.border.default }]}>
                        {checked && <Text style={styles.filterCheckMark}>✓</Text>}
                      </View>
                      <Text style={[styles.filterOptionText, checked && styles.filterOptionTextOn, isDark && { color: colors.text.secondary }, isDark && checked && { color: colors.brand.primary }]}>{opt}</Text>
                    </TouchableOpacity>
                  );
                })}
              </ScrollView>
              {columnFilters.activeStatus.length > 0 && (
                <TouchableOpacity style={styles.filterClearRow} onPress={() => { onColumnFilterChange?.('activeStatus', []); setFilterDropdown(null); }} activeOpacity={0.7}>
                  <Text style={styles.filterClearText}>Clear filter</Text>
                </TouchableOpacity>
              )}
            </>
          )}
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  tableContainer: {
    backgroundColor: AdminColors.bgSurface,
    borderRadius: 8,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.06,
    shadowRadius: 6,
    elevation: 2,
  },
  headerRow: {
    flexDirection: 'row',
    borderBottomWidth: 2,
    borderBottomColor: AdminColors.borderTable,
    backgroundColor: AdminColors.bgSurface,
  },
  headerCell: {
    padding: 8,
    justifyContent: 'center',
    // alignItems: 'center',
  },
  headerContent: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 4,
  },
  headerText: {
    fontSize: 10,
    fontWeight: '700',
    color: AdminColors.gray450,
    fontFamily: FontFamily.bold,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  headerTextActive: {
    color: AdminColors.blue700,
  },
  sortArrows: {
    gap: -4,
  },
  arrow: {
    fontSize: 8,
    color: AdminColors.gray300,
    opacity: 0.3,
  },
  arrowActive: {
    color: AdminColors.blue700,
    opacity: 1,
  },
  trainingHeader: {
    backgroundColor: AdminColors.blue50,
    borderRadius: 4,
    paddingVertical: 3,
    alignItems: 'center',
    marginBottom: 4,
  },
  trainingHeaderText: {
    fontSize: 10,
    fontWeight: '700',
    color: AdminColors.blue700,
    fontFamily: FontFamily.bold,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  trainingSubHeaders: {
    flexDirection: 'row',
    gap: 1,
  },
  trainingSubCell: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 4,
    gap: 2,
  },
  trainingRow: {
    flexDirection: 'row',
    gap: 1,
    flex: 1,
  },
  trainingCell: {
    justifyContent: 'center',
  },
  tableBody: {
    maxHeight: 600,
  },
  loadMoreRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    paddingVertical: 14,
    borderTopWidth: 1,
    borderTopColor: AdminColors.borderTable,
  },
  loadMoreText: {
    fontSize: 12,
    color: AdminColors.blue700,
    fontFamily: FontFamily.regular,
  },
  noMoreText: {
    fontSize: 12,
    color: AdminColors.gray350,
    fontFamily: FontFamily.regular,
  },
  emptyState: {
    paddingVertical: 40,
    alignItems: 'center',
    justifyContent: 'center',
  },
  emptyStateText: {
    fontSize: 14,
    color: AdminColors.gray450,
    fontFamily: FontFamily.regular,
  },
  // ── Column filter styles ───────────────────────────────────────────────────
  filterHeaderTop: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 2,
  },
  filterTrigger: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    width: 22,
    height: 22,
    borderRadius: 4,
    gap: 1,
  },
  filterTriggerActive: {
    backgroundColor: AdminColors.blue25,
  },
  filterCountBadge: {
    backgroundColor: AdminColors.blue700,
    borderRadius: 8,
    minWidth: 14,
    height: 14,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 3,
  },
  filterCountText: {
    fontSize: 9,
    color: '#fff',
    fontFamily: FontFamily.bold,
    fontWeight: '700',
  },
  floatingDropdown: {
    position: 'absolute',
    minWidth: 180,
    maxWidth: 240,
    backgroundColor: AdminColors.bgSurface,
    borderWidth: 1,
    borderColor: AdminColors.borderDefault,
    borderRadius: 8,
    paddingVertical: 4,
    paddingHorizontal: 4,
    shadowColor: '#3A3A3A',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.12,
    shadowRadius: 16,
    elevation: 20,
    zIndex: 100,
  },
  filterOptionsList: {
    maxHeight: 180,
    // Ensure the native scrollbar is visible on web
    ...(Platform.OS === 'web' ? { overflowY: 'scroll' } as any : {}),
  },
  filterOption: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingVertical: 4,
    paddingHorizontal: 2,
  },
  filterCheckbox: {
    width: 14,
    height: 14,
    borderRadius: 3,
    borderWidth: 1.5,
    borderColor: AdminColors.gray300,
    alignItems: 'center',
    justifyContent: 'center',
  },
  filterCheckboxOn: {
    backgroundColor: AdminColors.blue700,
    borderColor: AdminColors.blue700,
  },
  filterCheckMark: {
    fontSize: 9,
    color: '#fff',
    fontWeight: '700',
  },
  filterOptionText: {
    fontSize: 11,
    color: AdminColors.gray450,
    fontFamily: FontFamily.regular,
    flex: 1,
  },
  filterOptionTextOn: {
    color: AdminColors.blue700,
    fontFamily: FontFamily.medium,
  },
  filterClearRow: {
    borderTopWidth: 1,
    borderTopColor: AdminColors.borderTable,
    paddingVertical: 5,
    paddingHorizontal: 2,
    marginTop: 2,
    alignItems: 'center',
  },
  filterClearText: {
    fontSize: 10,
    color: AdminColors.blue700,
    fontFamily: FontFamily.medium,
    fontWeight: '600',
  },
  row: {
    flexDirection: 'row',
    borderBottomWidth: 2,
    borderBottomColor: AdminColors.borderTable,
    minHeight: 48,
    alignItems: 'center',
    backgroundColor: AdminColors.bgSurface,
  },
  rowSelected: {
    backgroundColor: AdminColors.blue25,
  },
  cell: {
    padding: 8,
    justifyContent: 'center',
    // alignItems: 'center',
  },
  cellText: {
    fontSize: 12,
    lineHeight: 16,
    color: AdminColors.textPrimary,
    fontFamily: FontFamily.regular,
  },
  badge: {
    paddingHorizontal: 8,
    paddingVertical: 2,
    height: 20,
    borderRadius: 12,
    alignSelf: 'flex-start',
    justifyContent: 'center',
    alignItems: 'center',
  },
  badgeYes: {
    backgroundColor: AdminColors.blue75,
  },
  badgeNo: {
    backgroundColor: AdminColors.gray50,
  },
  badgeText: {
    fontSize: 11,
    fontWeight: '500',
    fontFamily: FontFamily.medium,
  },
  badgeTextYes: {
    color: AdminColors.blue900,
  },
  badgeTextNo: {
    color: AdminColors.gray600,
  },
});

export default UserTable;
